import React, { useRef, useState } from 'react'
import { Link } from 'react-router-dom'
import { motion } from 'framer-motion'
import { Mail, Phone, MapPin, Linkedin, ExternalLink, Shield } from 'lucide-react'
import { useFirestore } from '@hooks/useFirestore'

const Footer = () => {
  const currentYear = new Date().getFullYear()
  const subscribers = useFirestore('newsletterSubscribers', { subscribe: false })

  // This form previously had no onSubmit at all: submitting reloaded the page
  // and the address went nowhere, so no subscriber was ever recorded.
  const [email, setEmail] = useState('')
  const [state, setState] = useState('idle') // idle | saving | done | error
  const [honeypot, setHoneypot] = useState('')
  const mountedAtRef = useRef(Date.now())

  const handleSubscribe = async (e) => {
    e.preventDefault()
    if (state === 'saving') return

    // Same spam gate as the contact form: hidden field plus a minimum dwell
    // time. Both fail quietly so bots get no signal to tune against.
    if (honeypot || Date.now() - mountedAtRef.current < 2500) {
      setState('done')
      setEmail('')
      return
    }

    const value = email.trim()
    if (!value) return

    setState('saving')
    try {
      await subscribers.addItem({
        email: value,
        source: 'footer',
        subscribedAt: new Date().toISOString(),
        status: 'active'
      })
      setState('done')
      setEmail('')
      setTimeout(() => setState('idle'), 4000)
    } catch (err) {
      console.error('Newsletter subscribe failed:', err)
      setState('error')
    }
  }

  const footerSections = [
    {
      title: 'Services',
      links: [
        { name: 'Equity Compensation & LTIs', href: '/services#equity' },
        { name: 'Executive Compensation & IPO', href: '/services#executive' },
        { name: 'Variable Pay & Sales Comp', href: '/services#variable' },
        { name: 'Comprehensive Benefits', href: '/services#benefits' },
        { name: 'Compensation Consulting', href: '/services#benchmarking' },
        { name: 'All Services', href: '/services' }
      ]
    },
    {
      title: 'Company',
      links: [
        { name: 'About Us', href: '/about' },
        { name: 'Our Team', href: '/about#team' },
        { name: 'Certification', href: '/certification' },
        { name: 'Careers', href: '/careers' },
        { name: 'Privacy Policy', href: '/privacy' },
        { name: 'Terms of Service', href: '/terms' }
      ]
    }
  ]

  const contactInfo = [
    {
      icon: Mail,
      label: 'Email',
      value: 'info@totalrewardspro.com',
      href: 'mailto:info@totalrewardspro.com'
    },
    {
      icon: Phone,
      label: 'Phone',
      value: '+91-9873655887',
      href: 'tel:+919873655887'
    },
    {
      icon: MapPin,
      label: 'Office',
      value: 'New Delhi, India',
      href: '/contact'
    }
  ]

  const socialLinks = [
    {
      name: 'LinkedIn',
      icon: Linkedin,
      href: 'https://linkedin.com/company/totalrewardspro',
      color: 'hover:text-blue-600'
    }
  ]

  return (
    <footer className="bg-navy-950 text-white print:hidden">
      <div className="w-full px-6 sm:px-8 lg:px-12">
        {/* Main Footer Content */}
        <div className="section-padding border-b border-navy-800/50">
          <div className="grid grid-cols-1 lg:grid-cols-3 gap-12 lg:gap-16">
            {/* Company Info */}
            <div className="lg:col-span-1">
              <Link to="/" className="flex items-center space-x-3 group mb-6">
                <img 
                  src="/TRP_logo_compressed.png" 
                  alt="Total Rewards Pro Logo" 
                  className="w-16 h-10 object-contain group-hover:scale-105 transition-transform duration-300"
                />
                <div>
                  <h3 className="text-xl font-open-sans font-semibold text-white">
                    Total Rewards Pro
                  </h3>
                  <p className="text-sm text-primary-400 font-medium">
                    People. Purpose. Pay. Perfectly Aligned.
                  </p>
                </div>
              </Link>
              
              <p className="text-neutral-300 text-body mb-8 leading-relaxed">
                Rewarding Success, Strategically. Your boutique partner for data-driven, 
                tailored total rewards solutions that align compensation and benefits with 
                business strategy.
              </p>

              {/* Contact Information */}
              <div className="space-y-4 mb-8">
                {contactInfo.map((item, index) => (
                  <motion.a
                    key={index}
                    href={item.href}
                    className="flex items-center space-x-3 text-neutral-300 hover:text-primary-400 transition-colors duration-200 group"
                    whileHover={{ x: 4 }}
                    transition={{ type: 'spring', stiffness: 400, damping: 25 }}
                  >
                    <div className="w-8 h-8 bg-gradient-to-r from-primary-500/20 to-primary-600/20 rounded-lg flex items-center justify-center group-hover:from-primary-500/30 group-hover:to-primary-600/30 transition-all duration-200">
                      <item.icon className="w-4 h-4 text-primary-400 group-hover:text-primary-300 transition-colors" />
                    </div>
                    <span className="text-sm font-medium">{item.value}</span>
                  </motion.a>
                ))}
              </div>

              {/* Social Links */}
              <div className="space-y-3">
                {socialLinks.map((social, index) => (
                  <motion.a
                    key={index}
                    href={social.href}
                    target="_blank"
                    rel="noopener noreferrer"
                    className="flex items-center space-x-3 text-neutral-300 hover:text-primary-400 transition-colors duration-200 group"
                    whileHover={{ x: 4 }}
                    transition={{ type: 'spring', stiffness: 400, damping: 25 }}
                  >
                    <div className="w-8 h-8 bg-gradient-to-r from-primary-500/20 to-primary-600/20 rounded-lg flex items-center justify-center group-hover:from-primary-500/30 group-hover:to-primary-600/30 transition-all duration-200">
                      <social.icon className="w-4 h-4 text-primary-400 group-hover:text-primary-300 transition-colors" />
                    </div>
                    <span className="text-sm font-medium">Follow us on {social.name}</span>
                  </motion.a>
                ))}
              </div>
            </div>

            {/* Footer Links */}
            <div className="lg:col-span-1">
              <div className="grid grid-cols-1 sm:grid-cols-2 gap-8">
                {footerSections.map((section, index) => (
                  <div key={index}>
                    <h4 className="text-lg font-open-sans font-semibold text-white mb-6 relative">
                      {section.title}
                      <div className="absolute -bottom-2 left-0 w-8 h-0.5 bg-gradient-to-r from-primary-500 to-primary-600 rounded-full"></div>
                    </h4>
                    <ul className="space-y-3">
                      {section.links.map((link, linkIndex) => (
                        <li key={linkIndex}>
                          <Link
                            to={link.href}
                            className="text-neutral-300 hover:text-primary-400 transition-colors duration-200 text-sm group flex items-center hover:translate-x-1"
                          >
                            <span className="w-1 h-1 bg-primary-500 rounded-full mr-3 opacity-0 group-hover:opacity-100 transition-opacity duration-200"></span>
                            {link.name}
                            {link.href.startsWith('http') && (
                              <ExternalLink className="w-3 h-3 ml-1 opacity-0 group-hover:opacity-100 transition-opacity" />
                            )}
                          </Link>
                        </li>
                      ))}
                    </ul>
                  </div>
                ))}
              </div>
            </div>

            {/* Newsletter Signup */}
            <div className="lg:col-span-1">
              <div className="bg-gradient-to-br from-primary-500/10 to-primary-600/10 rounded-xl p-8 border border-primary-500/20 backdrop-blur-sm">
                <div className="flex items-center space-x-3 mb-4">
                  <div className="w-12 h-12 bg-gradient-to-r from-primary-500 to-primary-600 rounded-xl flex items-center justify-center">
                    <Mail className="w-6 h-6 text-white" />
                  </div>
                  <div>
                    <h4 className="text-xl font-open-sans font-semibold text-white">
                      Stay Informed
                    </h4>
                    <p className="text-sm text-primary-400">Join 2,500+ professionals</p>
                  </div>
                </div>
                
                <p className="text-neutral-300 text-body mb-6 leading-relaxed">
                  Get monthly insights on compensation trends, regulatory updates, and strategic total rewards innovations.
                </p>
                
                <form className="space-y-4" onSubmit={handleSubscribe}>
                  <div
                    aria-hidden="true"
                    style={{
                      position: 'absolute',
                      width: '1px',
                      height: '1px',
                      overflow: 'hidden',
                      clip: 'rect(0 0 0 0)',
                      whiteSpace: 'nowrap'
                    }}
                  >
                    <label htmlFor="footer-website">Leave this field empty</label>
                    <input
                      id="footer-website"
                      name="website"
                      type="text"
                      tabIndex={-1}
                      autoComplete="off"
                      value={honeypot}
                      onChange={(e) => setHoneypot(e.target.value)}
                    />
                  </div>

                  <label htmlFor="footer-newsletter-email" className="sr-only">
                    Email address
                  </label>
                  <input
                    id="footer-newsletter-email"
                    name="email"
                    type="email"
                    autoComplete="email"
                    value={email}
                    onChange={(e) => setEmail(e.target.value)}
                    placeholder="Enter your email address"
                    className="w-full px-4 py-3 bg-navy-800/50 border border-primary-500/30 rounded-xl text-white placeholder-neutral-400 focus:outline-none focus:ring-2 focus:ring-primary-500 focus:border-primary-500 transition-all duration-200 backdrop-blur-sm"
                    required
                  />
                  <button
                    type="submit"
                    disabled={state === 'saving'}
                    className="w-full bg-gradient-to-r from-primary-500 to-primary-600 text-white font-semibold py-3 px-6 rounded-xl hover:from-primary-600 hover:to-primary-700 transition-all duration-200 shadow-lg hover:shadow-primary-500/25 disabled:opacity-60 disabled:cursor-not-allowed"
                  >
                    {state === 'saving' ? 'Subscribing…' : state === 'done' ? 'Subscribed' : 'Subscribe Now'}
                  </button>
                </form>

                <p className="text-xs text-neutral-400 mt-4 text-center" role="status" aria-live="polite">
                  {state === 'done'
                    ? "You're on the list. We'll be in touch."
                    : state === 'error'
                      ? 'That did not go through. Please try again.'
                      : 'No spam. Unsubscribe anytime.'}
                </p>
              </div>
            </div>
          </div>
        </div>

        {/* Bottom Footer */}
        <div className="py-6">
          <div className="flex flex-col md:flex-row items-center justify-between space-y-4 md:space-y-0">
            <div className="flex flex-col md:flex-row items-center space-y-2 md:space-y-0 md:space-x-6 text-sm text-neutral-400">
              <p>
                © {currentYear} iTotal Rewards Pro LLP. All rights reserved.
              </p>
              <div className="flex items-center space-x-4">
                <Link 
                  to="/privacy" 
                  className="hover:text-primary-400 transition-colors duration-200"
                >
                  Privacy Policy
                </Link>
                <span className="text-neutral-600">•</span>
                <Link 
                  to="/terms" 
                  className="hover:text-primary-400 transition-colors duration-200"
                >
                  Terms of Service
                </Link>
                <span className="text-neutral-600">•</span>
                <Link 
                  to="/admin/login" 
                  className="flex items-center space-x-1 hover:text-primary-400 transition-colors duration-200"
                  title="Admin Dashboard"
                >
                  <Shield className="w-3 h-3" />
                  <span>Admin</span>
                </Link>
              </div>
            </div>
            
            <div className="text-sm text-neutral-400 text-center md:text-right">
              <p>
                Designed & Built with Excellence by{' '}
                <a 
                  href="https://www.bindalinfotech.com" 
                  target="_blank" 
                  rel="noopener noreferrer"
                  className="text-primary-400 hover:text-primary-300 transition-colors duration-200 font-medium"
                >
                  Bindal Infotech
                  <ExternalLink className="w-3 h-3 inline ml-1" />
                </a>
              </p>
            </div>
          </div>
        </div>
      </div>
    </footer>
  )
}

export default Footer